import type { MouseEventHandler } from "react";
import { useCallback } from "react";
import { useIntl } from "react-intl";
import { Snackbar } from "@akashnetwork/ui/components";
import { usePopup } from "@akashnetwork/ui/context";
import { useSnackbar } from "notistack";

import { useServices } from "@src/context/ServicesProvider";
import { useCustomUser } from "@src/hooks/useCustomUser";

export const useEmailVerificationRequiredEventHandler = (): ((messageOtherwise: string) => (callback: MouseEventHandler) => MouseEventHandler) => {
  const intl = useIntl();
  const { requireAction } = usePopup();
  const { user } = useCustomUser();
  const { enqueueSnackbar } = useSnackbar();
  const { auth, analyticsService } = useServices();

  return useCallback(
    (messageOtherwise: string) => (handler: MouseEventHandler) => {
      const preventer: MouseEventHandler = event => {
        event.preventDefault();
        requireAction({
          message: messageOtherwise,
          actions: ({ close }) => [
            {
              label: intl.formatMessage({ id: "hooks.emailverification.sendcode", defaultMessage: "Send verification code" }),
              side: "left",
              size: "lg",
              onClick: () => {
                analyticsService.track("send_verification_code_btn_clk", "Amplitude");

                auth
                  .sendVerificationCode()
                  .then(() => {
                    enqueueSnackbar(
                      <Snackbar
                        title={intl.formatMessage({ id: "hooks.emailverification.codesent.title", defaultMessage: "Verification code sent" })}
                        subTitle={intl.formatMessage({
                          id: "hooks.emailverification.codesent.subtitle",
                          defaultMessage: "Please check your email for the 6-digit code and verify in the onboarding page"
                        })}
                        iconVariant="success"
                      />,
                      {
                        variant: "success"
                      }
                    );
                  })
                  .catch(() => {
                    enqueueSnackbar(
                      <Snackbar
                        title={intl.formatMessage({ id: "hooks.emailverification.codefailed.title", defaultMessage: "Failed to send code" })}
                        subTitle={intl.formatMessage({
                          id: "hooks.emailverification.codefailed.subtitle",
                          defaultMessage: "Please try again later or contact support"
                        })}
                        iconVariant="error"
                      />,
                      {
                        variant: "error"
                      }
                    );
                  })
                  .finally(close);
              }
            }
          ]
        });
      };

      return user?.emailVerified ? handler : preventer;
    },
    [user?.emailVerified, requireAction, enqueueSnackbar, auth, analyticsService, intl]
  );
};
